import React from "react";
import {
  Box,
  Typography,
  Grid,
  Avatar,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";

// Icons
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import VerifiedIcon from "@mui/icons-material/Verified";

export default function CustomerDataView({ customer }) {
  const theme = useTheme();

  if (!customer) {
    return (
      <Typography color="text.secondary">No customer data available</Typography>
    );
  }

  // Get first letter for avatar
  const firstLetter = customer.customerName?.charAt(0)?.toUpperCase() || "C";

  // Format phone number if needed
  const formatPhoneNumber = (phone) => {
    if (!phone) return "N/A";
    // Add your phone formatting logic here if needed
    return phone;
  };

  // Customer status (you can modify this based on your actual data)
  const getCustomerStatus = () => {
    // Example logic - modify based on your needs
    if (customer.status === "INACTIVE")
      return { label: "Inactive", color: "error" };
    return { label: "Active", color: "success" };
  };

  const status = getCustomerStatus();

  return (
    <Box>
      {/* Customer Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          p: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          borderRadius: 2,
        }}
      >
        <Avatar
          sx={{
            width: 56,
            height: 56,
            bgcolor: theme.palette.primary.main,
            fontSize: "1.5rem",
            fontWeight: 600,
          }}
        >
          {firstLetter}
        </Avatar>
        <Box sx={{ ml: 2, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" fontWeight="600">
              {customer.customerName}
            </Typography>
            {customer.isVerified && (
              <VerifiedIcon sx={{ fontSize: 20, color: "primary.main" }} />
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
            <Chip
              label={status.label}
              color={status.color}
              size="small"
              sx={{ fontWeight: 600 }}
            />
            {customer.customerType && (
              <Chip
                label={customer.customerType}
                variant="outlined"
                size="small"
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Customer Details */}
      <Grid container spacing={2}>
        {/* Email */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              p: 1.5,
              bgcolor: alpha(theme.palette.background.default, 0.5),
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <EmailIcon
              sx={{
                fontSize: 20,
                color: "text.secondary",
                mr: 2,
              }}
            />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Email Address
              </Typography>
              <Typography variant="body2" fontWeight="500">
                {customer.email || "Not provided"}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Phone */}
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              p: 1.5,
              bgcolor: alpha(theme.palette.background.default, 0.5),
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              height: "100%",
            }}
          >
            <PhoneIcon
              sx={{
                fontSize: 20,
                color: "text.secondary",
                mr: 2,
              }}
            />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Phone Number
              </Typography>
              <Typography variant="body2" fontWeight="500">
                {formatPhoneNumber(customer.phone)}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Customer ID */}
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              p: 1.5,
              bgcolor: alpha(theme.palette.background.default, 0.5),
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              height: "100%",
            }}
          >
            <BusinessIcon
              sx={{
                fontSize: 20,
                color: "text.secondary",
                mr: 2,
              }}
            />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Customer ID
              </Typography>
              <Typography variant="body2" fontWeight="500">
                {customer.customerCode || "N/A"}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Address */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              p: 1.5,
              bgcolor: alpha(theme.palette.background.default, 0.5),
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <LocationOnIcon
              sx={{
                fontSize: 20,
                color: "text.secondary",
                mr: 2,
                mt: 0.5,
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Address
              </Typography>
              <Typography variant="body2" fontWeight="500">
                {customer.address || "No address provided"}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Additional Info Section */}
        {(customer.registrationDate || customer.lastPurchase) && (
          <>
            {/* Registration Date */}
            {customer.registrationDate && (
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 1.5,
                    bgcolor: alpha(theme.palette.info.main, 0.05),
                    borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                  }}
                >
                  <CalendarTodayIcon
                    sx={{
                      fontSize: 20,
                      color: "info.main",
                      mr: 2,
                    }}
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Member Since
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="500"
                      color="info.main"
                    >
                      {new Date(customer.registrationDate).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Last Purchase */}
            {customer.lastPurchase && (
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 1.5,
                    bgcolor: alpha(theme.palette.success.main, 0.05),
                    borderRadius: 1,
                    border: `1px solid ${alpha(
                      theme.palette.success.main,
                      0.2
                    )}`,
                  }}
                >
                  <BusinessIcon
                    sx={{
                      fontSize: 20,
                      color: "success.main",
                      mr: 2,
                    }}
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Last Purchase
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="500"
                      color="success.main"
                    >
                      {new Date(customer.lastPurchase).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </>
        )}

        {/* Notes or Additional Information */}
        {customer.notes && (
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.warning.main, 0.05),
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={0.5}
              >
                Notes
              </Typography>
              <Typography variant="body2">{customer.notes}</Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
